import { Fragment, useState } from 'react';
import { useI18nText } from '@/lib/utils/i18nText';
import Tooltip from '@/components/Tooltip';
import {
  GgcDialog,
  GgcDialogContent,
  GgcDialogHeader,
  GgcDialogTitle,
  GgcDialogDescription,
} from '@/components/ui/ggc/dialog';
import { GgcLinkedText } from './ui/ggc/linked-text';

/**
 * Influencer status-flow guide — a single reusable dialog that visualises how a
 * campaign influencer moves through the shipped statuses (Candidate → … →
 * Completed) plus every branch/exit path, with a transition-rule table below.
 *
 * The node set is kept in sync with `CAMPAIGN_INFLUENCER_STATUSES`
 * (`src/lib/constants/influencerStatuses.ts`): the settlement stage is split
 * into Waiting For Payment → Payment Done → Completed, and Incomplete is drawn
 * as an exit — matching the real status list rather than the simplified draft.
 *
 * Two trigger variants share the same dialog:
 *  - `variant="icon"` — a small help (?) icon, e.g. next to the sidebar's
 *    "Influencer status" header.
 *  - `variant="link"` — an icon + label link, e.g. on the campaign progress
 *    toolbar between the status tabs and the budget bar.
 */

type Tone = 'start' | 'flow' | 'hold' | 'exit' | 'done';

const NODE_W = 184;
const NODE_H = 46;

const TONE: Record<Tone, { stroke: string; fill: string; text: string; bar?: string; dashed?: boolean }> = {
  start: { stroke: '#16202B', fill: '#FBFCFD', text: '#16202B', bar: '#16202B' },
  flow: { stroke: '#0F6E68', fill: '#FBFCFD', text: '#0F6E68', bar: '#0F6E68' },
  hold: { stroke: '#9A6510', fill: '#FBFCFD', text: '#9A6510', bar: '#9A6510' },
  exit: { stroke: '#9E3B47', fill: '#FBFCFD', text: '#9E3B47', dashed: true },
  done: { stroke: '#14503F', fill: '#14503F', text: '#FBFCFD' },
};

// Status names are shown in English only, regardless of app language — same rule
// as the sidebar/progress status labels.
const NODES: { key: string; label: string; x: number; y: number; tone: Tone }[] = [
  { key: 'candidate', label: 'Candidate', x: 44, y: 66, tone: 'start' },
  { key: 'contacting', label: 'Contacting', x: 44, y: 166, tone: 'flow' },
  { key: 'invited', label: 'Invited', x: 44, y: 266, tone: 'flow' },
  { key: 'negotiation', label: 'Negotiation', x: 44, y: 366, tone: 'flow' },
  { key: 'content_creation', label: 'Content Creation', x: 44, y: 500, tone: 'flow' },
  { key: 'payment', label: 'Waiting For Payment', x: 44, y: 634, tone: 'flow' },
  { key: 'payment_done', label: 'Payment Done', x: 44, y: 734, tone: 'flow' },
  { key: 'completed', label: 'Completed', x: 44, y: 834, tone: 'done' },
  { key: 'prospect', label: 'Prospect', x: 452, y: 118, tone: 'hold' },
  { key: 'deleted', label: 'Deleted', x: 452, y: 208, tone: 'exit' },
  { key: 'rejected', label: 'Rejected', x: 452, y: 336, tone: 'exit' },
  { key: 'incomplete', label: 'Incomplete', x: 452, y: 500, tone: 'exit' },
];

type EdgeTone = 'flow' | 'hold' | 'exit' | 'done' | 'ink';

const MARKER: Record<EdgeTone, string> = {
  flow: 'isf-aFlow', hold: 'isf-aHold', exit: 'isf-aExit', done: 'isf-aDone', ink: 'isf-aInk',
};
const STROKE: Record<EdgeTone, string> = {
  flow: '#0F6E68', hold: '#9A6510', exit: '#9E3B47', done: '#14503F', ink: '#5C6B7A',
};

const EDGES: { d: string; tone: EdgeTone; dashed?: boolean; w?: number }[] = [
  // entry
  { d: 'M136,36 V66', tone: 'ink' },
  // spine (happy path)
  { d: 'M136,112 V166', tone: 'flow', w: 1.6 },
  { d: 'M136,212 V266', tone: 'flow', w: 1.6 },
  { d: 'M136,312 V366', tone: 'flow', w: 1.6 },
  { d: 'M136,412 V500', tone: 'flow', w: 1.6 },
  { d: 'M136,546 V634', tone: 'flow', w: 1.6 },
  { d: 'M136,680 V734', tone: 'flow', w: 1.6 },
  { d: 'M136,780 V834', tone: 'done', w: 1.6 },
  // candidate → prospect / deleted
  { d: 'M228,82 H344 V141 H452', tone: 'hold' },
  { d: 'M228,100 H300 V231 H452', tone: 'exit' },
  // prospect → candidate / deleted / contacting
  { d: 'M452,128 Q 344,50 228,86', tone: 'hold', dashed: true },
  { d: 'M544,164 V208', tone: 'exit' },
  { d: 'M452,152 H360 V189 H228', tone: 'flow', dashed: true },
  // invited / negotiation → rejected
  { d: 'M228,289 H344 V352 H452', tone: 'exit' },
  { d: 'M228,389 H392 V372 H452', tone: 'exit' },
  // content creation → incomplete (drop-out)
  { d: 'M228,523 H452', tone: 'exit' },
];

const SANS = 'Pretendard, Apple SD Gothic Neo, sans-serif';

// Edge labels are terse; the transition-rule table below carries the full text.
const LABELS: { x: number; y: number; en: string; ko: string; fill: string; anchor?: 'start' | 'middle' | 'end'; size?: number }[] = [
  { x: 148, y: 54, en: 'Marketer registers candidate', ko: '마케터가 후보자 등록', fill: '#5C6B7A' },
  { x: 148, y: 143, en: 'Advertiser approved', ko: '광고주 승인', fill: '#3E4C59' },
  { x: 148, y: 243, en: 'Outreach sent', ko: '참여 요청 발송', fill: '#3E4C59' },
  { x: 148, y: 343, en: 'Start negotiation', ko: '협상 시작', fill: '#3E4C59' },
  { x: 148, y: 444, en: 'Negotiation succeeds', ko: '협상 타결', fill: '#3E4C59' },
  { x: 148, y: 575, en: 'Production done', ko: '제작 완료', fill: '#3E4C59' },
  { x: 148, y: 711, en: 'Payment settled', ko: '정산 완료', fill: '#3E4C59' },
  { x: 148, y: 811, en: 'Campaign wrapped up', ko: '캠페인 종료', fill: '#3E4C59' },
  { x: 350, y: 104, en: 'hold decision', ko: '보류', fill: '#9A6510', size: 10.5 },
  { x: 306, y: 202, en: 'advertiser rejected', ko: '광고주 거절', fill: '#9E3B47', size: 10.5 },
  { x: 300, y: 44, en: 'needs re-review', ko: '재검토 필요', fill: '#9A6510', anchor: 'middle', size: 10.5 },
  { x: 552, y: 192, en: 'no longer needed', ko: '참여 불필요', fill: '#9E3B47', size: 10.5 },
  { x: 332, y: 176, en: 'marketer decision', ko: '마케터 결정', fill: '#0F6E68', anchor: 'middle', size: 10.5 },
  { x: 350, y: 322, en: 'declines', ko: '참여 거절', fill: '#9E3B47', size: 10.5 },
  { x: 398, y: 406, en: 'negotiation fails', ko: '협상 결렬', fill: '#9E3B47', size: 10.5 },
  { x: 300, y: 514, en: "can't finish", ko: '미완주', fill: '#9E3B47', size: 10.5 },
];

const PHASES: { line: number; labelY: number; en: string; ko: string }[] = [
  { line: 26, labelY: 18, en: '01 — CAMPAIGN SETUP', ko: '01 — 캠페인 세팅' },
  { line: 464, labelY: 456, en: '02 — CAMPAIGN IN PROGRESS', ko: '02 — 캠페인 진행' },
  { line: 598, labelY: 590, en: '03 — CAMPAIGN SETTLEMENT', ko: '03 — 캠페인 정산' },
];

function InfluencerStatusFlowDiagram() {
  const { t } = useI18nText();
  return (
    <svg viewBox="0 0 760 910" role="img" aria-label={t('Influencer status transition diagram', '인플루언서 상태 전이 다이어그램')} className="status-flow-svg">
      <defs>
        <pattern id="isf-grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M20 0 L0 0 0 20" fill="none" stroke="#C9D2DA" strokeWidth=".5" opacity=".45" />
        </pattern>
        {(Object.keys(STROKE) as EdgeTone[]).map((tone) => (
          <marker key={tone} id={MARKER[tone]} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto">
            <path d="M0 0 L8 4 L0 8 z" fill={STROKE[tone]} />
          </marker>
        ))}
      </defs>

      <rect x="0" y="0" width="760" height="910" fill="url(#isf-grid)" />

      {/* phase bands */}
      {PHASES.map((p) => (
        <g key={p.en}>
          <text x="20" y={p.labelY} fontFamily="ui-monospace, SF Mono, Menlo, monospace" fontSize="10.5" letterSpacing="1.4" fill="#5C6B7A">
            {t(p.en, p.ko)}
          </text>
          <path d={`M20 ${p.line} L740 ${p.line}`} stroke="#C9D2DA" strokeWidth="1" />
        </g>
      ))}

      {/* edges */}
      {EDGES.map((e, i) => (
        <path
          key={i}
          d={e.d}
          fill="none"
          stroke={STROKE[e.tone]}
          strokeWidth={e.w ?? 1.4}
          strokeDasharray={e.dashed ? '3 3' : undefined}
          markerEnd={`url(#${MARKER[e.tone]})`}
        />
      ))}

      {/* edge labels */}
      {LABELS.map((l, i) => (
        <text key={i} x={l.x} y={l.y} fontFamily={SANS} fontSize={l.size ?? 11.5} fill={l.fill} textAnchor={l.anchor ?? 'start'}>
          {t(l.en, l.ko)}
        </text>
      ))}

      {/* nodes */}
      {NODES.map((n) => {
        const tone = TONE[n.tone];
        return (
          <g key={n.key}>
            <rect
              x={n.x}
              y={n.y}
              width={NODE_W}
              height={NODE_H}
              rx={4}
              fill={tone.fill}
              stroke={tone.stroke}
              strokeWidth={1.2}
              strokeDasharray={tone.dashed ? '4 3' : undefined}
            />
            {tone.bar && <path d={`M${n.x + 2} ${n.y + 2} L${n.x + 2} ${n.y + NODE_H - 2}`} stroke={tone.bar} strokeWidth={4} />}
            <text
              x={n.x + NODE_W / 2}
              y={n.y + NODE_H / 2 + 4}
              textAnchor="middle"
              fill={tone.text}
              fontFamily="ui-monospace, SF Mono, Menlo, monospace"
              fontSize={12}
              fontWeight={600}
            >
              {n.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const LEGEND: { cls: string; en: string; ko: string }[] = [
  { cls: 'is-start', en: 'Start', ko: '시작 상태' },
  { cls: 'is-flow', en: 'In progress', ko: '진행 중' },
  { cls: 'is-hold', en: 'On hold', ko: '보류' },
  { cls: 'is-exit', en: 'Exited / excluded', ko: '종료 · 제외' },
  { cls: 'is-done', en: 'Completed', ko: '종료 · 완료' },
];

// Transition rules — condition text is bilingual; status names stay English.
const RULE_SECTIONS: { en: string; ko: string; rows: { from?: string; to: string; en: string; ko: string }[] }[] = [
  {
    en: '01 Campaign setup',
    ko: '01 캠페인 세팅',
    rows: [
      { to: 'Candidate', en: 'Marketer registers a recommended candidate', ko: '마케터가 후보자 추천 등록' },
      { from: 'Candidate', to: 'Contacting', en: 'Advertiser approved + marketer contacts right away', ko: '광고주 Approved + 마케터가 즉시 컨택 판단' },
      { from: 'Candidate', to: 'Prospect', en: 'Advertiser approved or rejected + marketer holds the decision', ko: '광고주 Approved 또는 Rejected + 마케터가 보류 판단' },
      { from: 'Candidate', to: 'Deleted', en: 'Advertiser rejected + marketer excludes', ko: '광고주 Rejected + 마케터가 제외 판단' },
      { from: 'Prospect', to: 'Candidate', en: 'Marketer sends it back for advertiser re-review', ko: '마케터 판단으로 광고주 재판단이 다시 필요한 경우' },
      { from: 'Prospect', to: 'Deleted', en: 'Marketer decides participation is no longer needed', ko: '마케터 판단으로 캠페인 참여 필요가 없어진 경우' },
      { from: 'Prospect', to: 'Contacting', en: 'Marketer decides to proceed, via direct outreach', ko: '마케터 판단 또는 별도 연락으로 참여를 결정한 경우' },
      { from: 'Contacting', to: 'Invited', en: 'Marketer sends the participation request', ko: '마케터가 캠페인 참여 요청 연락 발송' },
      { from: 'Invited', to: 'Negotiation', en: 'Terms negotiation begins (marketer · agency · influencer)', ko: '마케터·에이전시·인플루언서 간 조건 협상 착수' },
      { from: 'Invited', to: 'Rejected', en: 'Influencer declines to participate', ko: '인플루언서 측 참여 거절' },
      { from: 'Negotiation', to: 'Rejected', en: "Negotiation fails or participation can't proceed", ko: '계약 협상 불가 또는 참여 불가 확정' },
    ],
  },
  {
    en: '02 Campaign in progress',
    ko: '02 캠페인 진행',
    rows: [
      { from: 'Negotiation', to: 'Content Creation', en: 'Negotiation succeeds, influencer joins the campaign', ko: '협상 타결, 인플루언서 캠페인 참여 시작' },
      { from: 'Content Creation', to: 'Incomplete', en: "Can't finish mid-campaign — illness, legal issue, etc.", ko: '질병·소송 등으로 캠페인 미완주' },
    ],
  },
  {
    en: '03 Campaign settlement',
    ko: '03 캠페인 정산',
    rows: [
      { from: 'Content Creation', to: 'Waiting For Payment', en: 'Video / stream production done, awaiting payment', ko: '영상·스트림 제작 완료, 정산 대기' },
      { from: 'Waiting For Payment', to: 'Payment Done', en: 'Influencer payment settled', ko: '인플루언서 정산 완료 처리' },
      { from: 'Payment Done', to: 'Completed', en: 'Campaign wrapped up for this influencer', ko: '해당 인플루언서 캠페인 종료' },
    ],
  },
];

function StatusFlowDialogBody() {
  const { t } = useI18nText();
  return (
    <div className="status-flow-body">
      <div className="status-flow-sheet">
        <InfluencerStatusFlowDiagram />
        <div className="status-flow-legend">
          {LEGEND.map((l) => (
            <span key={l.cls}>
              <i className={l.cls} />
              {t(l.en, l.ko)}
            </span>
          ))}
        </div>
      </div>

      <table className="status-flow-rules">
        <thead>
          <tr>
            <th>{t('Transition', '전이')}</th>
            <th>{t('Condition · actor', '조건 · 주체')}</th>
          </tr>
        </thead>
        <tbody>
          {RULE_SECTIONS.map((section) => (
            <Fragment key={section.en}>
              <tr className="status-flow-rules-phase">
                <td colSpan={2}>{t(section.en, section.ko)}</td>
              </tr>
              {section.rows.map((r, i) => (
                <tr key={`${section.en}-${i}`}>
                  <td>
                    {r.from && <code>{r.from}</code>}
                    <span className="status-flow-arrow">→</span>
                    <code>{r.to}</code>
                  </td>
                  <td>{t(r.en, r.ko)}</td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function InfluencerStatusFlowGuide({ variant = 'icon' }: { variant?: 'icon' | 'link' }) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const tip = t('Influencer status flow guide', '인플루언서 상태 흐름 가이드');

  const trigger =
    variant === 'link' ? (
      <span className='flex items-center gap-1'>
        <i className="fa fa-question-circle ggc-text-caption-s" aria-hidden />
        <GgcLinkedText
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          aria-label={tip}
          className="flex gap-1 items-center w-fit ggc-text-caption-s text-gray-500 ! cursor-pointer"
        >
          <span>{t('Status Flow', '상태 플로우')}</span>
        </GgcLinkedText>
      </span>
    ) : (
      <button
        type="button"
        className="status-flow-help-btn"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        aria-label={tip}
      >
        <i className="fa fa-question-circle" aria-hidden />
      </button>
    );

  return (
    <>
      <Tooltip content={tip}>{trigger}</Tooltip>
      <GgcDialog open={open} onOpenChange={setOpen}>
        <GgcDialogContent className="status-flow-dialog" closeLabel={t('Close', '닫기')}>
          <GgcDialogHeader>
            <GgcDialogTitle>{t('Influencer status flow', '인플루언서 상태 전이 흐름')}</GgcDialogTitle>
            <GgcDialogDescription>
              {t(
                'How an influencer moves from candidate to completed, and every branch / exit path.',
                '후보자부터 정산 완료까지 인플루언서가 거치는 상태와 분기 · 이탈 경로입니다.',
              )}
            </GgcDialogDescription>
          </GgcDialogHeader>
          <StatusFlowDialogBody />
        </GgcDialogContent>
      </GgcDialog>
    </>
  );
}

export default InfluencerStatusFlowGuide;
